import React, { useState, useEffect } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider } from 'firebase/auth';
import { doc,
  setDoc } from 'firebase/firestore';
import { Container,
  Button,
  Typography,
  Box,
  Paper,
  Link,
  TextField,
  IconButton,
  InputAdornment } from '@mui/material';
import { Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { authStyles } from '../common/AuthStyles';
import { color, r } from '../../theme/tokens';
import { logAnalyticsEvent, logGoogleAdsConversion, GOOGLE_ADS_CONVERSIONS } from '../../utils/analytics';
import config from '../../config';
import { handleFirebaseSignIn, initializeGoogleSignIn, initializeAppleSignIn } from '../auth/AuthUtils';
import AppleSignInButton from '../auth/AppleSignInButton';
import '../auth/GoogleButton.css';

function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [formValid, setFormValid] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Method selection, 2: Email form
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const googleProvider = new GoogleAuthProvider();

  useEffect(() => {
    // Initialize native sign-in handlers (called back by the iOS shell)
    initializeGoogleSignIn(navigate, setMessage);
    initializeAppleSignIn(navigate, setMessage);
  }, [navigate]);

  useEffect(() => {
    const { email, password, confirmPassword } = formData;
    const passwordsMatch = password === confirmPassword && password.length >= 6;

    if (email && password && confirmPassword && passwordsMatch) {
      setFormValid(true);
    } else {
      setFormValid(false);
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleGoogleSignIn = async () => {
    try {
      // Log lightweight terms acknowledgement for Google
      logAnalyticsEvent('terms_acknowledged', { method: 'google' });
      const result = await signInWithPopup(auth, googleProvider);
      
      const credential = GoogleAuthProvider.credentialFromResult(result);
      
      // Pass the credential to handleFirebaseSignIn and wait for the result
      const signInSuccess = await handleFirebaseSignIn(credential, navigate, setMessage);

      // If the sign-in was successful, the AuthUtils will handle navigation
      if (signInSuccess) {
        // Navigation is handled by AuthUtils (either to /complete-registration or /dashboard)
        return;
      }

    } catch (error) {
      console.error('Google sign-in error:', error);
      setMessage(`Unable to sign in with Google: ${error.message}`);
      // Log failed Google registration attempt
      logAnalyticsEvent('sign_up', { method: 'google', status: 'failure', error_message: error.message });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { email, password } = formData;

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create basic user document with completion flag
      await setDoc(doc(db, 'users', user.uid), {
        email,
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: '',
        createdAt: new Date().toISOString(),
        needsProfileCompletion: true // Flag for email users
      });

      // Log successful registration
      logAnalyticsEvent('sign_up', { method: 'email', status: 'success' });
      logGoogleAdsConversion(GOOGLE_ADS_CONVERSIONS.SIGN_UP);

      // Navigate to completion page instead of immediate trial
      setMessage('Account created! Please complete your profile...');
      navigate('/complete-registration');
      
    } catch (error) {
      // Handle specific error cases with user-friendly messages
      let errorMessage = 'Registration failed. Please try again.';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters long.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection.';
          break;
        // Don't expose other Firebase errors to the user
      }
      
      setMessage(errorMessage);
      // Log failed registration
      logAnalyticsEvent('sign_up', { method: 'email', status: 'failure', error_code: error.code, error_message: errorMessage });
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={0} sx={authStyles.paper}>
        <Typography component="h1" variant="h4" sx={authStyles.title}>
          Welcome! Let's get started
        </Typography>

        {/* Step indicator with progress dots */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          mb: 1,
          mt: 1
        }}>
                     {/* Progress dots */}
           <Box sx={{ 
             display: 'flex', 
             alignItems: 'center',
             mb: 1
           }}>
             <Box sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               backgroundColor: color.accent,
               mr: 1
             }} />
             <Box sx={{
               width: 16,
               height: 2,
               backgroundColor: color.line,
               mr: 1
             }} />
             <Box sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               backgroundColor: color.line,
               mr: 1
             }} />
             <Box sx={{
               width: 16,
               height: 2,
               backgroundColor: color.line,
               mr: 1
             }} />
             <Box sx={{
               width: 8,
               height: 8,
               borderRadius: '50%',
               backgroundColor: color.line
             }} />
           </Box>
          
          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 500
          }}>
            Step {currentStep} of 3
          </Typography>
        </Box>

        {/* Authentication Options Container */}
        <Box sx={{
          border: `${r(1)} solid`,
          borderColor: 'divider',
          borderRadius: '0.75rem',
          p: 3,
          mb: 3,
          backgroundColor: 'background.paper'
        }}>
          {currentStep === 1 ? (
            // Step 1: Method Selection
            <>
              {/* Google Sign-in Section */}
              <Box sx={{ mb: 3 }}>
                {/* Social sign-in — native flows inside the iOS shell, popups in browsers */}
                <Box className="social-auth-buttons" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.5, mb: 2.5 }}>
                  <button
                    type="button"
                    className="gsi-material-button"
                    onClick={handleGoogleSignIn}
                  >
                    <div className="gsi-material-button-state"></div>
                    <div className="gsi-material-button-content-wrapper">
                      <div className="gsi-material-button-icon">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ display: 'block' }}>
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                          <path fill="none" d="M0 0h48v48H0z"></path>
                        </svg>
                      </div>
                      <span className="gsi-material-button-contents">Continue with Google</span>
                    </div>
                  </button>

                  <AppleSignInButton setMessage={setMessage} />
                </Box>

                {/* Divider */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  my: 2.5
                }}>
                  <Box sx={{ flex: 1, borderBottom: `0.0625rem solid ${color.line}` }} />
                  <Typography variant="body1" sx={{ px: '1rem', color: 'text.secondary' }}>
                    or
                  </Typography>
                  <Box sx={{ flex: 1, borderBottom: `0.0625rem solid ${color.line}` }} />
                </Box>

                {/* Continue with Email Button */}
                <Button
                  fullWidth
                  onClick={() => setCurrentStep(2)}
                  sx={{
                    ...authStyles.secondaryButton,
                    border: `${r(2)} solid`,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      border: `${r(2)} solid`,
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  Continue with Email
                </Button>
              </Box>

              {/* Disclaimer inside the container */}
              <Box sx={{
                borderTop: `${r(1)} solid`,
                borderColor: 'divider',
                pt: 2,
                mt: 2
              }}>
                <Typography variant="caption" align="center" sx={{
                  color: 'text.secondary',
                  lineHeight: 1.4,
                  fontSize: '0.75rem'
                }}>
                  By continuing with any of the options above, you agree to our{' '}
                  <Link href="/terms-of-use" target="_blank">Terms of Use</Link> and{' '}
                  <Link href="/privacy-policy" target="_blank">Privacy Policy</Link>.
                </Typography>
              </Box>
            </>
          ) : (
          // Step 2: Email Form
          <>
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={() => setCurrentStep(1)}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: 'primary.main'
                  }
                }}
                startIcon={
                  <Typography variant="body2" sx={{ fontSize: '1.2rem', mr: 0.5 }}>
                    ←
                  </Typography>
                }
              >
                Back to options
              </Button>
            </Box>

            <Box component="form" onSubmit={handleRegister} noValidate sx={{ mt: 2 }}>
              {/* Simplified registration form - only email and password */}
              <TextField
                variant="outlined"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                sx={{ ...authStyles.textField, mb: 2.5 }}
              />

              <TextField
                variant="outlined"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                sx={{ ...authStyles.textField, mb: 2.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                variant="outlined"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                sx={{ ...authStyles.textField, mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={!formValid}
                sx={{
                  ...authStyles.secondaryButton,
                  border: `${r(2)} solid`,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  fontWeight: 600,
                  mt: 3,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    border: `${r(2)} solid`,
                    borderColor: 'primary.main',
                  },
                  '&:disabled': {
                    borderColor: 'action.disabled',
                    color: 'action.disabled'
                  }
                }}
              >
                Continue
              </Button>

              {message && (
                <Typography color="error" sx={authStyles.errorMessage}>
                  {message}
                </Typography>
              )}

            </Box>
          </>
        )}
        </Box>

      </Paper>
    </Container>
  );
}

export default Register; 